import React, { useState, useRef } from "react";
import type { EditorElement } from "../../../editor/types";
import { renderCanvasElementContent } from "../lib/renderers";
import RichTextEditor from "./RichTextEditor";
import { resolveTextStyle } from "../lib/appearance";

interface CellElementProps {
  element: EditorElement;
  cellWidth: number;
  cellHeight: number;
  isSelected: boolean;
  onUpdate: (updatedElement: EditorElement) => void;
  onDelete: () => void;
  onSelect: (elementId: string) => void;
  onContextMenu: (elementId: string, x: number, y: number) => void;
}

type ResizeHandle =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "top"
  | "right"
  | "bottom"
  | "left";

export const CellElement: React.FC<CellElementProps> = ({
  element,
  cellWidth,
  cellHeight,
  isSelected,
  onUpdate,
  onDelete,
  onSelect,
  onContextMenu,
}) => {
  // Debug: Log cell dimensions
  React.useEffect(() => {
    console.log('CellElement dimensions:', {
      cellWidth,
      cellHeight,
      elementId: element.id,
      elementSize: element.size
    });
  }, [cellWidth, cellHeight, element.id, element.size]);

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle | null>(null);
  // Inline rich-text editing for text elements inside the cell.
  const [isEditingText, setIsEditingText] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, y: 0, elementX: 0, elementY: 0 });
  const resizeStartRef = useRef({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    elementX: 0,
    elementY: 0,
    currentWidth: 0,
    currentHeight: 0,
    currentX: 0,
    currentY: 0,
    aspectRatioLocked: false,
  });

  const handleDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (element.type === "text") {
      event.stopPropagation();
      setIsEditingText(true);
    }
  };

  const handleRichTextSave = (htmlContent: string, plainText: string) => {
    onUpdate({
      ...element,
      data: { ...element.data, content: plainText, htmlContent },
    });
    setIsEditingText(false);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();

    // While editing text, let the editor handle the pointer (place caret,
    // select text) and don't start a drag.
    if (isEditingText) {
      return;
    }

    // Select this element in the global state
    onSelect(element.id);

    setIsDragging(true);
    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      elementX: element.position.x,
      elementY: element.position.y,
    };
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isDragging) {
      const deltaX = event.clientX - dragStartRef.current.x;
      const deltaY = event.clientY - dragStartRef.current.y;

      let newX = dragStartRef.current.elementX + deltaX;
      let newY = dragStartRef.current.elementY + deltaY;

      // Constrain to cell boundaries
      const elementWidth = element.size.width ?? 100;
      const elementHeight = element.size.height ?? 50;

      const maxY = cellHeight - elementHeight;
      console.log('Drag move:', {
        deltaY,
        newY,
        cellHeight,
        elementHeight,
        maxY,
        willBeConstrainedTo: Math.max(0, Math.min(newY, maxY))
      });

      newX = Math.max(0, Math.min(newX, cellWidth - elementWidth));
      newY = Math.max(0, Math.min(newY, cellHeight - elementHeight));

      onUpdate({
        ...element,
        position: { x: newX, y: newY },
      });
    } else if (isResizing && resizeHandle) {
      const wantsAspectRatioLock =
        event.shiftKey &&
        resizeStartRef.current.currentWidth > 0 &&
        resizeStartRef.current.currentHeight > 0;

      if (resizeStartRef.current.aspectRatioLocked !== wantsAspectRatioLock) {
        resizeStartRef.current = {
          ...resizeStartRef.current,
          x: event.clientX,
          y: event.clientY,
          width: resizeStartRef.current.currentWidth,
          height: resizeStartRef.current.currentHeight,
          elementX: resizeStartRef.current.currentX,
          elementY: resizeStartRef.current.currentY,
          aspectRatioLocked: wantsAspectRatioLock,
        };
      }

      const deltaX = event.clientX - resizeStartRef.current.x;
      const deltaY = event.clientY - resizeStartRef.current.y;

      let newWidth = resizeStartRef.current.width;
      let newHeight = resizeStartRef.current.height;
      let newX = resizeStartRef.current.elementX;
      let newY = resizeStartRef.current.elementY;
      const minWidth = Math.max(element.size.minWidth ?? 20, 20);
      const minHeight = Math.max(element.size.minHeight ?? 20, 20);
      const startWidth = resizeStartRef.current.width;
      const startHeight = resizeStartRef.current.height;
      const shouldLockAspectRatio =
        resizeStartRef.current.aspectRatioLocked &&
        startWidth > 0 &&
        startHeight > 0;

      if (shouldLockAspectRatio) {
        let widthScale: number | null = null;
        let heightScale: number | null = null;

        if (resizeHandle.includes("right")) {
          widthScale = (startWidth + deltaX) / startWidth;
        } else if (resizeHandle.includes("left")) {
          widthScale = (startWidth - deltaX) / startWidth;
        }

        if (resizeHandle.includes("bottom")) {
          heightScale = (startHeight + deltaY) / startHeight;
        } else if (resizeHandle.includes("top")) {
          heightScale = (startHeight - deltaY) / startHeight;
        }

        const dominantScale =
          widthScale === null
            ? heightScale ?? 1
            : heightScale === null
              ? widthScale
              : Math.abs(widthScale - 1) >= Math.abs(heightScale - 1)
                ? widthScale
                : heightScale;

        const minScale = Math.max(
          minWidth / startWidth,
          minHeight / startHeight
        );
        const maxScaleFromWidth = resizeHandle.includes("left")
          ? (resizeStartRef.current.elementX + startWidth) / startWidth
          : (cellWidth - resizeStartRef.current.elementX) / startWidth;
        const maxScaleFromHeight = resizeHandle.includes("top")
          ? (resizeStartRef.current.elementY + startHeight) / startHeight
          : (cellHeight - resizeStartRef.current.elementY) / startHeight;
        const maxScale = Math.max(
          minScale,
          Math.min(maxScaleFromWidth, maxScaleFromHeight)
        );
        const lockedScale = Math.min(
          maxScale,
          Math.max(minScale, dominantScale)
        );

        newWidth = startWidth * lockedScale;
        newHeight = startHeight * lockedScale;

        if (resizeHandle.includes("left")) {
          newX =
            resizeStartRef.current.elementX + (startWidth - newWidth);
        }
        if (resizeHandle.includes("top")) {
          newY =
            resizeStartRef.current.elementY + (startHeight - newHeight);
        }
      } else {
        // Handle different resize directions
        if (resizeHandle.includes("right")) {
          newWidth = resizeStartRef.current.width + deltaX;
        }
        if (resizeHandle.includes("left")) {
          newWidth = resizeStartRef.current.width - deltaX;
          newX = resizeStartRef.current.elementX + deltaX;
        }
        if (resizeHandle.includes("bottom")) {
          newHeight = resizeStartRef.current.height + deltaY;
        }
        if (resizeHandle.includes("top")) {
          newHeight = resizeStartRef.current.height - deltaY;
          newY = resizeStartRef.current.elementY + deltaY;
        }

        // Apply minimum size constraints
        newWidth = Math.max(minWidth, newWidth);
        newHeight = Math.max(minHeight, newHeight);
      }

      // Constrain to cell boundaries
      if (newX < 0) {
        newWidth += newX;
        newX = 0;
      }
      if (newY < 0) {
        newHeight += newY;
        newY = 0;
      }
      if (newX + newWidth > cellWidth) {
        newWidth = cellWidth - newX;
      }
      if (newY + newHeight > cellHeight) {
        newHeight = cellHeight - newY;
      }

      resizeStartRef.current.currentWidth = newWidth;
      resizeStartRef.current.currentHeight = newHeight;
      resizeStartRef.current.currentX = newX;
      resizeStartRef.current.currentY = newY;

      onUpdate({
        ...element,
        position: { x: newX, y: newY },
        size: { ...element.size, width: newWidth, height: newHeight },
      });
    }
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isDragging || isResizing) {
      (event.target as HTMLElement).releasePointerCapture(event.pointerId);
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
    }
  };

  const handleResizeStart = (
    event: React.PointerEvent<HTMLDivElement>,
    handle: ResizeHandle
  ) => {
    event.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
    resizeStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      width: element.size.width ?? 100,
      height: element.size.height ?? 50,
      elementX: element.position.x,
      elementY: element.position.y,
      currentWidth: element.size.width ?? 100,
      currentHeight: element.size.height ?? 50,
      currentX: element.position.x,
      currentY: element.position.y,
      aspectRatioLocked:
        event.shiftKey &&
        (element.size.width ?? 100) > 0 &&
        (element.size.height ?? 50) > 0,
    };
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      onDelete();
    }
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // Select this element first
    onSelect(element.id);

    // Open context menu for this cell element
    onContextMenu(element.id, event.clientX, event.clientY);
  };

  return (
    <div
      ref={elementRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        position: "absolute",
        left: element.position.x,
        top: element.position.y,
        width: element.size.width ?? 100,
        height: element.size.height ?? 50,
        cursor: isEditingText
          ? "text"
          : isDragging
          ? "grabbing"
          : "grab",
        outline: isSelected ? "2px solid #0066cc" : "none",
        outlineOffset: "2px",
        boxSizing: "border-box",
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onContextMenu={handleContextMenu}
      onDoubleClick={handleDoubleClick}
      // Keep the click from bubbling to the cell (<td>), which would otherwise
      // clear the nested-element selection right after we set it.
      onClick={(e) => e.stopPropagation()}
    >
      {/* Element content (inline rich-text editor for text elements). */}
      {isEditingText && element.type === "text" ? (
        (() => {
          const textStyle = resolveTextStyle(element);
          const data = element.data as {
            content?: string;
            htmlContent?: string;
          };
          const initialContent = data.htmlContent || data.content || "";
          const editorStyle: React.CSSProperties = {
            width: "100%",
            height: "100%",
            fontFamily: textStyle.fontFamily,
            fontSize: `${textStyle.fontSize}px`,
            fontWeight: textStyle.fontWeight,
            color: textStyle.color,
            textAlign: textStyle.textAlign,
            lineHeight: textStyle.lineHeight,
            letterSpacing: `${textStyle.letterSpacing}px`,
            backgroundColor: textStyle.backgroundColor,
            padding: `${textStyle.paddingY}px ${textStyle.paddingX}px`,
            borderRadius: textStyle.borderRadius,
            border:
              textStyle.borderWidth > 0
                ? `${textStyle.borderWidth}px solid ${textStyle.borderColor}`
                : "none",
            textTransform: textStyle.textTransform,
            boxShadow: textStyle.shadow,
            boxSizing: "border-box",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          };
          return (
            <RichTextEditor
              initialContent={initialContent}
              style={editorStyle}
              onSave={handleRichTextSave}
              onCancel={() => setIsEditingText(false)}
            />
          );
        })()
      ) : (
        <div style={{ width: "100%", height: "100%", pointerEvents: "none" }}>
          {renderCanvasElementContent(element, { mode: "canvas" })}
        </div>
      )}

      {/* Resize handles - only show when selected and not editing text */}
      {isSelected && !isEditingText && (
        <>
          {/* Corner handles */}
          {["top-left", "top-right", "bottom-left", "bottom-right"].map(
            (handle) => (
              <div
                key={handle}
                onPointerDown={(e) => handleResizeStart(e, handle as ResizeHandle)}
                style={{
                  position: "absolute",
                  width: "8px",
                  height: "8px",
                  backgroundColor: "#0066cc",
                  border: "1px solid white",
                  borderRadius: "50%",
                  cursor: `${handle}-resize`,
                  ...(handle === "top-left" && { top: -4, left: -4 }),
                  ...(handle === "top-right" && { top: -4, right: -4 }),
                  ...(handle === "bottom-left" && { bottom: -4, left: -4 }),
                  ...(handle === "bottom-right" && { bottom: -4, right: -4 }),
                  zIndex: 10,
                }}
              />
            )
          )}

          {/* Edge handles */}
          {["top", "right", "bottom", "left"].map((handle) => (
            <div
              key={handle}
              onPointerDown={(e) => handleResizeStart(e, handle as ResizeHandle)}
              style={{
                position: "absolute",
                backgroundColor: "#0066cc",
                ...(handle === "top" && {
                  top: -2,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "20px",
                  height: "4px",
                  cursor: "ns-resize",
                }),
                ...(handle === "bottom" && {
                  bottom: -2,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "20px",
                  height: "4px",
                  cursor: "ns-resize",
                }),
                ...(handle === "left" && {
                  left: -2,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "4px",
                  height: "20px",
                  cursor: "ew-resize",
                }),
                ...(handle === "right" && {
                  right: -2,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "4px",
                  height: "20px",
                  cursor: "ew-resize",
                }),
                zIndex: 10,
              }}
            />
          ))}
        </>
      )}
    </div>
  );
};
